// Trusted Types: see web/packages/ui/src/security/trusted-types.ts and
// docs/security/csp-strategy.md. Must run before any React import so
// React's internal sink writes use the registered policy.
import { installTrustedTypesPolicies } from '@sentinel/ui';
installTrustedTypesPolicies();

import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import i18next from 'i18next';
import { createApiClient } from '@sentinel/api-client';
import { SessionProvider, getStepUpQueue, isFederatedUser, type AccessTokenSchedulerOptions } from '@sentinel/auth-core';
import { initI18n } from '@sentinel/i18n';
import { ErrorFallback, describeError } from '@sentinel/ui';
import '@VicOne-RD/miraidon-ui/index.css';
import { routeTree } from './routeTree.gen';
import loginEn from './features/login/i18n/en.json';
import loginZhTW from './features/login/i18n/zh-TW.json';
import loginZhCN from './features/login/i18n/zh-CN.json';
import loginJa from './features/login/i18n/ja.json';
import passkeyEn from './features/passkey/i18n/en.json';
import passkeyZhTW from './features/passkey/i18n/zh-TW.json';
import passkeyZhCN from './features/passkey/i18n/zh-CN.json';
import passkeyJa from './features/passkey/i18n/ja.json';
import logoutEn from './features/logout/i18n/en.json';
import logoutZhTW from './features/logout/i18n/zh-TW.json';
import logoutZhCN from './features/logout/i18n/zh-CN.json';
import logoutJa from './features/logout/i18n/ja.json';
import registerEn from './features/register/i18n/en.json';
import registerZhTW from './features/register/i18n/zh-TW.json';
import registerZhCN from './features/register/i18n/zh-CN.json';
import registerJa from './features/register/i18n/ja.json';
import welcomeEn from './features/welcome/i18n/en.json';
import welcomeZhTW from './features/welcome/i18n/zh-TW.json';
import welcomeZhCN from './features/welcome/i18n/zh-CN.json';
import welcomeJa from './features/welcome/i18n/ja.json';
import verifyEmailEn from './features/verify-email/i18n/en.json';
import verifyEmailZhTW from './features/verify-email/i18n/zh-TW.json';
import verifyEmailZhCN from './features/verify-email/i18n/zh-CN.json';
import verifyEmailJa from './features/verify-email/i18n/ja.json';
import magicLinkEn from './features/magic-link/i18n/en.json';
import magicLinkZhTW from './features/magic-link/i18n/zh-TW.json';
import magicLinkZhCN from './features/magic-link/i18n/zh-CN.json';
import magicLinkJa from './features/magic-link/i18n/ja.json';
import resetPasswordEn from './features/reset-password/i18n/en.json';
import resetPasswordZhTW from './features/reset-password/i18n/zh-TW.json';
import resetPasswordZhCN from './features/reset-password/i18n/zh-CN.json';
import resetPasswordJa from './features/reset-password/i18n/ja.json';
import invitationEn from './features/invitation/i18n/en.json';
import invitationZhTW from './features/invitation/i18n/zh-TW.json';
import invitationZhCN from './features/invitation/i18n/zh-CN.json';
import invitationJa from './features/invitation/i18n/ja.json';
import stepUpEn from './features/step-up/i18n/en.json';
import stepUpZhTW from './features/step-up/i18n/zh-TW.json';
import stepUpZhCN from './features/step-up/i18n/zh-CN.json';
import stepUpJa from './features/step-up/i18n/ja.json';
import mfaEn from './features/mfa/i18n/en.json';
import mfaZhTW from './features/mfa/i18n/zh-TW.json';
import mfaZhCN from './features/mfa/i18n/zh-CN.json';
import mfaJa from './features/mfa/i18n/ja.json';
import securityEn from './features/security/i18n/en.json';
import securityZhTW from './features/security/i18n/zh-TW.json';
import securityZhCN from './features/security/i18n/zh-CN.json';
import securityJa from './features/security/i18n/ja.json';
import sessionsEn from './features/sessions/i18n/en.json';
import sessionsZhTW from './features/sessions/i18n/zh-TW.json';
import sessionsZhCN from './features/sessions/i18n/zh-CN.json';
import sessionsJa from './features/sessions/i18n/ja.json';
import dataEn from './features/data/i18n/en.json';
import dataZhTW from './features/data/i18n/zh-TW.json';
import dataZhCN from './features/data/i18n/zh-CN.json';
import dataJa from './features/data/i18n/ja.json';
import consentEn from './features/consent/i18n/en.json';
import consentZhTW from './features/consent/i18n/zh-TW.json';
import consentZhCN from './features/consent/i18n/zh-CN.json';
import consentJa from './features/consent/i18n/ja.json';
import deviceCodeEn from './features/device-code/i18n/en.json';
import deviceCodeZhTW from './features/device-code/i18n/zh-TW.json';
import deviceCodeZhCN from './features/device-code/i18n/zh-CN.json';
import deviceCodeJa from './features/device-code/i18n/ja.json';
import accountEn from './features/account/i18n/en.json';
import accountZhTW from './features/account/i18n/zh-TW.json';
import accountZhCN from './features/account/i18n/zh-CN.json';
import accountJa from './features/account/i18n/ja.json';
import credentialsEn from './features/credentials/i18n/en.json';
import credentialsZhTW from './features/credentials/i18n/zh-TW.json';
import credentialsZhCN from './features/credentials/i18n/zh-CN.json';
import credentialsJa from './features/credentials/i18n/ja.json';
import mcpEn from './features/mcp/i18n/en.json';
import mcpZhTW from './features/mcp/i18n/zh-TW.json';
import mcpZhCN from './features/mcp/i18n/zh-CN.json';
import mcpJa from './features/mcp/i18n/ja.json';
import profileEn from './features/profile/i18n/en.json';
import profileZhTW from './features/profile/i18n/zh-TW.json';
import profileZhCN from './features/profile/i18n/zh-CN.json';
import profileJa from './features/profile/i18n/ja.json';
import connectedAppsEn from './features/connected-apps/i18n/en.json';
import connectedAppsZhTW from './features/connected-apps/i18n/zh-TW.json';
import connectedAppsZhCN from './features/connected-apps/i18n/zh-CN.json';
import connectedAppsJa from './features/connected-apps/i18n/ja.json';
import auditLogEn from './features/audit-log/i18n/en.json';
import auditLogZhTW from './features/audit-log/i18n/zh-TW.json';
import auditLogZhCN from './features/audit-log/i18n/zh-CN.json';
import auditLogJa from './features/audit-log/i18n/ja.json';
import impersonationEn from './features/impersonation/i18n/en.json';
import impersonationZhTW from './features/impersonation/i18n/zh-TW.json';
import impersonationZhCN from './features/impersonation/i18n/zh-CN.json';
import impersonationJa from './features/impersonation/i18n/ja.json';
import productsEn from './features/products/i18n/en.json';
import productsZhTW from './features/products/i18n/zh-TW.json';
import productsZhCN from './features/products/i18n/zh-CN.json';
import productsJa from './features/products/i18n/ja.json';

// Test-only: Playwright e2e can shorten refresh timing via `window.__sentinelTestOverrides`.
// Production builds simply receive `undefined` and the scheduler uses its defaults.
function readRefreshOverrides(): AccessTokenSchedulerOptions | undefined {
  const w = window as unknown as {
    __sentinelTestOverrides?: { refreshScheduler?: AccessTokenSchedulerOptions };
  };
  return w.__sentinelTestOverrides?.refreshScheduler;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });
const stepUpQueue = getStepUpQueue();
const apiClient = createApiClient({ stepUpQueue, isFederatedUser: () => isFederatedUser() });
const router = createRouter({
  routeTree,
  context: { queryClient, apiClient },
  defaultErrorComponent: ({ error, reset }) => {
    // `defaultErrorComponent` mounts before any provider tree, so we read
    // strings from the i18next singleton rather than `useTranslation()` —
    // the latter would be brittle if the boundary fires before init.
    const t = i18next.t.bind(i18next);
    const described = describeError(error);
    const message =
      described === 'Unknown error' ? t('common:errors.unknown', 'Unknown error') : described;
    return (
      <ErrorFallback
        title={t('common:errors.page_failed_to_load', 'Page failed to load')}
        message={message}
        retryLabel={t('common:errors.try_again', 'Try again')}
        onRetry={reset}
      />
    );
  },
});

declare module '@tanstack/react-router' { interface Register { router: typeof router } }

await initI18n({
  defaultNamespace: 'portal',
  appResources: {},
  extraNamespaces: {
    login: { en: loginEn, 'zh-TW': loginZhTW, 'zh-CN': loginZhCN, ja: loginJa },
    passkey: { en: passkeyEn, 'zh-TW': passkeyZhTW, 'zh-CN': passkeyZhCN, ja: passkeyJa },
    logout: { en: logoutEn, 'zh-TW': logoutZhTW, 'zh-CN': logoutZhCN, ja: logoutJa },
    register: { en: registerEn, 'zh-TW': registerZhTW, 'zh-CN': registerZhCN, ja: registerJa },
    welcome: { en: welcomeEn, 'zh-TW': welcomeZhTW, 'zh-CN': welcomeZhCN, ja: welcomeJa },
    'verify-email': {
      en: verifyEmailEn,
      'zh-TW': verifyEmailZhTW,
      'zh-CN': verifyEmailZhCN,
      ja: verifyEmailJa,
    },
    'magic-link': {
      en: magicLinkEn,
      'zh-TW': magicLinkZhTW,
      'zh-CN': magicLinkZhCN,
      ja: magicLinkJa,
    },
    'reset-password': {
      en: resetPasswordEn,
      'zh-TW': resetPasswordZhTW,
      'zh-CN': resetPasswordZhCN,
      ja: resetPasswordJa,
    },
    invitation: {
      en: invitationEn,
      'zh-TW': invitationZhTW,
      'zh-CN': invitationZhCN,
      ja: invitationJa,
    },
    'step-up': {
      en: stepUpEn,
      'zh-TW': stepUpZhTW,
      'zh-CN': stepUpZhCN,
      ja: stepUpJa,
    },
    mfa: {
      en: mfaEn,
      'zh-TW': mfaZhTW,
      'zh-CN': mfaZhCN,
      ja: mfaJa,
    },
    security: {
      en: securityEn,
      'zh-TW': securityZhTW,
      'zh-CN': securityZhCN,
      ja: securityJa,
    },
    sessions: {
      en: sessionsEn,
      'zh-TW': sessionsZhTW,
      'zh-CN': sessionsZhCN,
      ja: sessionsJa,
    },
    data: {
      en: dataEn,
      'zh-TW': dataZhTW,
      'zh-CN': dataZhCN,
      ja: dataJa,
    },
    consent: {
      en: consentEn,
      'zh-TW': consentZhTW,
      'zh-CN': consentZhCN,
      ja: consentJa,
    },
    'device-code': {
      en: deviceCodeEn,
      'zh-TW': deviceCodeZhTW,
      'zh-CN': deviceCodeZhCN,
      ja: deviceCodeJa,
    },
    account: {
      en: accountEn,
      'zh-TW': accountZhTW,
      'zh-CN': accountZhCN,
      ja: accountJa,
    },
    credentials: {
      en: credentialsEn,
      'zh-TW': credentialsZhTW,
      'zh-CN': credentialsZhCN,
      ja: credentialsJa,
    },
    mcp: { en: mcpEn, 'zh-TW': mcpZhTW, 'zh-CN': mcpZhCN, ja: mcpJa },
    profile: { en: profileEn, 'zh-TW': profileZhTW, 'zh-CN': profileZhCN, ja: profileJa },
    'connected-apps': {
      en: connectedAppsEn,
      'zh-TW': connectedAppsZhTW,
      'zh-CN': connectedAppsZhCN,
      ja: connectedAppsJa,
    },
    'audit-log': {
      en: auditLogEn,
      'zh-TW': auditLogZhTW,
      'zh-CN': auditLogZhCN,
      ja: auditLogJa,
    },
    impersonation: {
      en: impersonationEn,
      'zh-TW': impersonationZhTW,
      'zh-CN': impersonationZhCN,
      ja: impersonationJa,
    },
    products: {
      en: productsEn,
      'zh-TW': productsZhTW,
      'zh-CN': productsZhCN,
      ja: productsJa,
    },
  },
});

const schedulerOverrides = readRefreshOverrides();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SessionProvider client={apiClient} {...(schedulerOverrides ? { refreshSchedulerOptions: schedulerOverrides } : {})}>
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  </StrictMode>,
);
